import { useMemo } from 'react';
import { ActivityData, ClassroomStatus } from '../../../../interfaces/activity';
import { useClassroomEligibility } from './useClassroomEligibility';
import {
    parseViltMeetingStatus,
    ParsedViltMeetingStatus,
    ViltMeetingStatusType,
    ViltMeetingCoHost,
    getMeetingCoHosts,
    isWebexLicenseError,
} from './viltMeetingStatusUtils';
import {
    ActivityModality,
    ActivityLMSType,
    ActivityViltType,
} from '../../Common/Common';
import { CLASSROOM_CONFIG } from '../../Common/classroomEligibilityRules';

/**
 * Status indicator types matching Cloudscape StatusIndicator component
 */
export type StatusIndicatorType =
    | 'success'
    | 'error'
    | 'warning'
    | 'info'
    | 'stopped'
    | 'pending'
    | 'in-progress'
    | 'loading';

/**
 * Individual setup step with its current status
 */
export interface SetupStep {
    id: string;
    label: string;
    status: StatusIndicatorType;
    detail?: string;
    helpText?: string;
}

/**
 * Overall classroom setup status
 */
export type OverallSetupStatus =
    | 'not-started' // Eligibility requirements not met
    | 'eligible' // All requirements met, waiting for auto-creation
    | 'in-progress' // Classroom creation is in progress
    | 'completed' // Classroom is ready
    | 'error'; // Failed state

/**
 * Return type for the useClassroomSetupStatus hook
 */
export interface ClassroomSetupStatus {
    steps: SetupStep[];
    overallStatus: OverallSetupStatus;
    isEligible: boolean;
    hasClassroom: boolean;
    canRetry: boolean;
    classroomStatus: string | null;
    viltMeetingStatus: ParsedViltMeetingStatus;
    errorMessage: string | null;
    /**
     * True when the vILT meeting error is a missing WebEx host license. When
     * set, licenseRequiredCoHosts lists everyone who needs a license.
     */
    isWebexLicenseError: boolean;
    /**
     * Everyone the backend grants WebEx co-host access (instructors, additional
     * owners, operations owner). Each needs an active WebEx Meetings Suite
     * license on awsvirtual.webex.com. Surfaced so coordinators know exactly
     * who to provision when a license error occurs.
     */
    licenseRequiredCoHosts: ViltMeetingCoHost[];
}

/**
 * Maps classroom status to overall setup status
 */
const getOverallStatusFromClassroomStatus = (
    classroomStatus: string | null | undefined,
    isEligible: boolean,
    viltMeetingStatus: ParsedViltMeetingStatus,
): OverallSetupStatus => {
    // Check for error states first
    if (
        classroomStatus === ClassroomStatus.CLASSROOM_UPDATE_FAILED ||
        classroomStatus === ClassroomStatus.CLASSROOM_PROVISIONING_FAILED
    ) {
        return 'error';
    }

    if (viltMeetingStatus.type === 'error') {
        return 'error';
    }

    // Check for completed state
    if (classroomStatus === ClassroomStatus.CLASSROOM_READY) {
        return 'completed';
    }

    // Check for in-progress states
    if (
        classroomStatus === ClassroomStatus.READY_FOR_CLASSROOM_CREATION ||
        viltMeetingStatus.type === 'processing'
    ) {
        return 'in-progress';
    }

    // Check eligibility
    if (isEligible) {
        return 'eligible';
    }

    return 'not-started';
};

// Physical address fields Classrooms requires for an ILT classroom, with their form labels.
const CLASSROOM_ADDRESS_FIELDS: Array<[keyof ActivityData, string]> = [
    ['delivery_address_1', 'Address line 1'],
    ['delivery_city', 'City'],
    ['delivery_postal_code', 'Postal code'],
    ['delivery_country', 'Country'],
];

/**
 * Labels of the address fields Classrooms requires that are blank on an ILT activity. Classrooms
 * rejects every classroom update while any of these is empty.
 */
export const getBlankClassroomAddressFields = (
    activity: ActivityData,
): string[] => {
    if (activity?.activity_modality !== ActivityModality.ILT) {
        return [];
    }
    return CLASSROOM_ADDRESS_FIELDS.filter(
        ([field]) => String(activity[field] ?? '').trim() === '',
    ).map(([, label]) => label);
};

/**
 * Determines if the user can retry classroom creation
 */
const canRetryClassroomCreation = (
    classroomStatus: string | null | undefined,
    viltMeetingStatus: ParsedViltMeetingStatus,
    isUpdateOfExistingClassroom: boolean,
): boolean => {
    // Retry requests classroom creation, which does nothing for a classroom that already exists.
    // Its failed update is sent again automatically once the activity details are corrected.
    if (isUpdateOfExistingClassroom) {
        return false;
    }
    const failedStatuses = [
        ClassroomStatus.CLASSROOM_UPDATE_FAILED,
        ClassroomStatus.CLASSROOM_PROVISIONING_FAILED,
    ];

    return (
        (classroomStatus &&
            failedStatuses.includes(classroomStatus as ClassroomStatus)) ||
        viltMeetingStatus.type === 'error'
    );
};

/**
 * Gets the error message from various error sources
 */
const getErrorMessage = (
    classroomStatus: string | null | undefined,
    viltMeetingStatus: ParsedViltMeetingStatus,
    isUpdateOfExistingClassroom: boolean,
    blankAddressFields: string[],
): string | null => {
    if (viltMeetingStatus.type === 'error' && viltMeetingStatus.message) {
        return `vILT meeting creation failed: ${viltMeetingStatus.message}`;
    }

    if (isUpdateOfExistingClassroom) {
        if (blankAddressFields.length) {
            return (
                `Classroom update failed: ${blankAddressFields.join(', ')} ` +
                `${blankAddressFields.length === 1 ? 'is' : 'are'} empty. ` +
                'Fill in the delivery address and save; the classroom updates automatically.'
            );
        }
        return 'Classroom update failed. Please contact support.';
    }

    if (classroomStatus === ClassroomStatus.CLASSROOM_UPDATE_FAILED) {
        return 'Classroom update failed. Please try again or contact support.';
    }

    if (classroomStatus === ClassroomStatus.CLASSROOM_PROVISIONING_FAILED) {
        return 'Classroom provisioning failed. Please try again or contact support.';
    }

    return null;
};

/**
 * Custom hook that combines eligibility checks with classroom/vILT creation status
 * to provide a unified view of the classroom setup progress.
 *
 * @param activity - The activity data
 * @returns Complete classroom setup status including steps, overall status, and error info
 */
export const useClassroomSetupStatus = (
    activity: ActivityData,
): ClassroomSetupStatus => {
    const { isEligible, checks } = useClassroomEligibility(activity);
    const viltMeetingStatus = useMemo(
        () => parseViltMeetingStatus(activity?.v_ilt_meeting_creation_status),
        [activity?.v_ilt_meeting_creation_status],
    );

    return useMemo(() => {
        const classroomStatus = activity?.classrooms_status || null;
        const isVirtual = activity?.activity_modality !== ActivityModality.ILT;

        // "Other" vILT type means the meeting is managed outside Grimsby,
        // so we don't require auto-created vILT meeting for classroom creation.
        const usesAutoCreatedVilt =
            isVirtual && activity?.v_ilt_type !== ActivityViltType.Other;

        const hasClassroom =
            classroomStatus === ClassroomStatus.CLASSROOM_READY;

        const overallStatus = getOverallStatusFromClassroomStatus(
            classroomStatus,
            isEligible,
            viltMeetingStatus,
        );

        // The update of an existing classroom failed (as opposed to classroom creation)
        const isUpdateOfExistingClassroom =
            classroomStatus === ClassroomStatus.CLASSROOM_UPDATE_FAILED &&
            Boolean(activity?.classrooms_arn);

        const canRetry = canRetryClassroomCreation(
            classroomStatus,
            viltMeetingStatus,
            isUpdateOfExistingClassroom,
        );
        const errorMessage = getErrorMessage(
            classroomStatus,
            viltMeetingStatus,
            isUpdateOfExistingClassroom,
            getBlankClassroomAddressFields(activity),
        );

        // When the vILT meeting failed on a missing WebEx host license, surface
        // the full co-host list so coordinators know exactly who to provision.
        // The backend error names only "instructors" generically and never
        // identifies which account(s) failed verification.
        const webexLicenseError = isWebexLicenseError(viltMeetingStatus);
        const licenseRequiredCoHosts = webexLicenseError
            ? getMeetingCoHosts(activity)
            : [];

        // Build the steps array based on eligibility checks and creation status
        const steps: SetupStep[] = [];

        // Step 1: Activity Status
        steps.push({
            id: 'activity-status',
            label: 'Activity is Active',
            status: checks.isActive ? 'success' : 'pending',
            helpText: checks.isActive
                ? undefined
                : 'Set activity status to Active to enable classroom creation',
        });

        // Step 2: Instructor assigned
        steps.push({
            id: 'instructor',
            label: 'Instructor assigned',
            status: checks.hasInstructors ? 'success' : 'pending',
            helpText: checks.hasInstructors
                ? undefined
                : 'Assign at least one instructor',
        });

        // Step 3: Class size set
        steps.push({
            id: 'class-size',
            label: `Class size set${activity?.class_size ? ` (${activity.class_size})` : ''}`,
            status: checks.hasValidSize ? 'success' : 'pending',
            helpText: checks.hasValidSize
                ? undefined
                : 'Set class size to at least 1',
        });

        // Step 4: LMS Requirements (only for virtual activities)
        if (isVirtual) {
            // Get specific LMS requirements info
            const lmsType = activity?.lms_type;
            const lmsRequirements = lmsType
                ? CLASSROOM_CONFIG.LMS_REQUIREMENTS[lmsType as ActivityLMSType]
                : null;

            // Check which specific requirements are missing
            const getMissingRequirements = (): string[] => {
                if (!lmsType) return ['LMS type not set'];
                if (!lmsRequirements)
                    return [`${lmsType} LMS type not supported`];

                const missing: string[] = [];
                for (const req of lmsRequirements) {
                    if (req === 'vIltId' && !activity?.v_ilt_id) {
                        // Don't flag vILT URL as missing for "Other" type since
                        // the link may not be known at scheduling time.
                        if (activity?.v_ilt_type !== ActivityViltType.Other) {
                            missing.push('vILT URL');
                        }
                    }
                    if (
                        req === 'vIltMeetingId' &&
                        !activity?.v_ilt_meeting_id
                    ) {
                        missing.push('Meeting ID');
                    }
                }
                return missing;
            };

            // Generate the label based on LMS type and status
            const getLmsLabel = (): string => {
                if (checks.lmsRequirementsMet) {
                    if (lmsType) {
                        return `${lmsType} requirements met`;
                    }
                    return 'LMS requirements met';
                }

                const missing = getMissingRequirements();
                if (!lmsType) {
                    return 'LMS type not set';
                }
                if (missing.length === 0) {
                    return `${lmsType} requirements met`;
                }
                if (missing.length === 1) {
                    return `${lmsType}: Missing ${missing[0]}`;
                }
                return `${lmsType}: Missing ${missing.join(', ')}`;
            };

            const getLmsHelpText = (): string | undefined => {
                if (checks.lmsRequirementsMet) {
                    return undefined;
                }
                const missing = getMissingRequirements();
                if (!lmsType) {
                    return 'Select an LMS type in the Operations section';
                }
                if (missing.length > 0) {
                    return `Required for ${lmsType}: ${missing.join(', ')}`;
                }
                return undefined;
            };

            steps.push({
                id: 'lms-requirements',
                label: getLmsLabel(),
                status: checks.lmsRequirementsMet ? 'success' : 'pending',
                helpText: getLmsHelpText(),
            });
        }

        // Step 5: WebEx Meeting (only for virtual activities with auto-created vILT)
        if (usesAutoCreatedVilt) {
            const getViltStepStatus = (): StatusIndicatorType => {
                switch (viltMeetingStatus.type) {
                    case 'completed':
                        return 'success';
                    case 'processing':
                        return 'loading';
                    case 'error':
                        return 'error';
                    default:
                        // If not started, show pending if eligible, otherwise stopped
                        return isEligible ? 'pending' : 'stopped';
                }
            };

            const getViltStepLabel = (): string => {
                switch (viltMeetingStatus.type) {
                    case 'completed':
                        return 'vILT meeting created';
                    case 'processing':
                        return 'Creating vILT meeting...';
                    case 'error':
                        return 'vILT meeting failed';
                    default:
                        return 'vILT meeting';
                }
            };

            steps.push({
                id: 'webex-meeting',
                label: getViltStepLabel(),
                status: getViltStepStatus(),
                detail:
                    viltMeetingStatus.type === 'error'
                        ? viltMeetingStatus.message || undefined
                        : undefined,
                helpText:
                    viltMeetingStatus.type === 'none' && !isEligible
                        ? 'Complete the above requirements first'
                        : undefined,
            });
        }

        // Step 6: Classroom Creation
        // Determine if all prerequisites for classroom creation are met
        const allPrerequisitesMet =
            isEligible &&
            (!isVirtual ||
                activity?.v_ilt_type === ActivityViltType.Other ||
                viltMeetingStatus.type === 'completed');

        const getClassroomStepStatus = (): StatusIndicatorType => {
            if (hasClassroom) {
                return 'success';
            }
            if (
                classroomStatus === ClassroomStatus.CLASSROOM_UPDATE_FAILED ||
                classroomStatus ===
                    ClassroomStatus.CLASSROOM_PROVISIONING_FAILED
            ) {
                return 'error';
            }
            if (
                classroomStatus === ClassroomStatus.READY_FOR_CLASSROOM_CREATION
            ) {
                return 'loading';
            }
            // For virtual activities, wait for WebEx meeting
            if (usesAutoCreatedVilt && viltMeetingStatus.type !== 'completed') {
                return 'stopped';
            }
            // If all prerequisites are met but classroom isn't ready yet,
            // show loading to indicate classroom creation is in progress
            if (allPrerequisitesMet) {
                return 'loading';
            }
            // If eligible but waiting for something, show pending
            if (isEligible) {
                return 'pending';
            }
            return 'stopped';
        };

        const getClassroomStepLabel = (): string => {
            if (hasClassroom) {
                return 'Classroom ready';
            }
            if (isUpdateOfExistingClassroom) {
                return 'Classroom update failed';
            }
            if (
                classroomStatus === ClassroomStatus.CLASSROOM_UPDATE_FAILED ||
                classroomStatus ===
                    ClassroomStatus.CLASSROOM_PROVISIONING_FAILED
            ) {
                return 'Classroom creation failed';
            }
            if (
                classroomStatus ===
                    ClassroomStatus.READY_FOR_CLASSROOM_CREATION ||
                allPrerequisitesMet
            ) {
                return 'Creating classroom...';
            }
            return 'Classroom';
        };

        const getClassroomHelpText = (): string | undefined => {
            if (hasClassroom) {
                return undefined;
            }
            if (usesAutoCreatedVilt && viltMeetingStatus.type !== 'completed') {
                return 'Waiting for vILT meeting';
            }
            if (!isEligible) {
                return 'Complete the above requirements first';
            }
            if (allPrerequisitesMet) {
                return undefined; // No help needed when creating
            }
            return 'Classroom will be created automatically';
        };

        steps.push({
            id: 'classroom',
            label: getClassroomStepLabel(),
            status: getClassroomStepStatus(),
            helpText: getClassroomHelpText(),
        });

        return {
            steps,
            overallStatus,
            isEligible,
            hasClassroom,
            canRetry,
            classroomStatus,
            viltMeetingStatus,
            errorMessage,
            isWebexLicenseError: webexLicenseError,
            licenseRequiredCoHosts,
        };
    }, [
        activity?.classrooms_status,
        activity?.classrooms_arn,
        activity?.delivery_address_1,
        activity?.delivery_city,
        activity?.delivery_postal_code,
        activity?.delivery_country,
        activity?.activity_modality,
        activity?.class_size,
        activity?.lms_type,
        activity?.v_ilt_id,
        activity?.v_ilt_meeting_id,
        activity?.v_ilt_type,
        activity?.instructors,
        activity?.additional_owners,
        activity?.operations_owner,
        activity?.operations_owner_email,
        isEligible,
        checks,
        viltMeetingStatus,
    ]);
};
